// OpsOverviewHost — the impure shell edge of the Ops home (`/ops`). It owns
// the clock ("today" and "this month" are anchored when the page opens),
// the reads (the app's /healthz + /readyz, independent of any workspace
// data so the page loads even when the app cannot read state; the AI usage
// summary for today and for the month; the feedback list) and the frame.
// Each read is its own Remote, so each part waits and fails alone.

export { OpsOverviewHost };

import { Result } from "@bilda/utils";
import { useState } from "react";
import { OpsOverviewPage } from "../../components/Ops/OpsOverviewPage.js";
import { TooltipProvider } from "../../components/Tooltip/Tooltip.js";
import { aiUsageApi, aiUsageQueryString } from "../aiUsage/api.js";
import { periodRange } from "../aiUsage/period.js";
import {
    type AiUsageRoute,
    type PeriodPreset,
    serializeAiUsageRoute,
} from "../aiUsage/route.js";
import { opsApi } from "./api.js";
import { isDenied, useOpsFrame, useRemote } from "./hooks.js";

const routeFor = (period: PeriodPreset): AiUsageRoute => ({
    tab: "overview",
    period,
    filters: {},
    sort: "time",
    dir: "desc",
    page: 0,
    by: "workspace",
});

const OpsOverviewHost = () => {
    const frame = useOpsFrame();
    const [nowMs] = useState(() => Date.now());
    const [attempt, setAttempt] = useState(0);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const summaryOf = (period: PeriodPreset) => () =>
        aiUsageApi.summary(
            aiUsageQueryString(
                routeFor(period),
                periodRange(period, nowMs),
                tz,
            ),
        );
    const today = useRemote(summaryOf("day"), attempt);
    const month = useRemote(summaryOf("month"), attempt);
    const feedback = useRemote(opsApi.feedback, attempt);
    const health = useRemote(
        () => opsApi.health().then((h) => Result.ok(h)),
        attempt,
    );

    const denied = [today, month, feedback].some(
        (r) => r._tag === "Remote.Failed" && isDenied(r.error),
    );
    return (
        <TooltipProvider>
            <OpsOverviewPage
                frame={frame}
                access={denied ? "denied" : "allowed"}
                health={health}
                today={today}
                month={month}
                feedback={feedback}
                failedCallsHref={serializeAiUsageRoute({
                    ...routeFor("day"),
                    tab: "calls",
                    filters: { outcome: "failed" },
                })}
                aiUsageHref={serializeAiUsageRoute(routeFor("month"))}
                nowMs={nowMs}
                onRetry={() => setAttempt((n) => n + 1)}
            />
        </TooltipProvider>
    );
};
